"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";

import { Inbox, Settings, Users } from "lucide-react";

import { Alert, AlertDescription } from "@/shared/ui/feedback/alert";
import { Button } from "@/shared/ui/buttons/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/overlays/tooltip";

import { DRAFT_GATED, DRAFT_SOON_HINT } from "./draft-gating";

/** Куда порталить кнопки: слот в шапке карточки вкладки «Драфт». */
export const DRAFT_ACTIONS_SLOT_ID = "draft-actions-slot";

type ResolvedView = "organizer" | "captain" | "participant" | "spectator";
type ViewOverride = "auto" | ResolvedView;

interface DraftHeaderProps {
  resolvedView: ResolvedView;
  isSiteAdmin: boolean;
  canManage: boolean;
  riotDisabledReason?: string | null;
  /**
   * KAN-366: Discord-интеграция турнира. Кнопка войса ведёт к распределению по
   * голосовым каналам, которых при выключенной интеграции не существует — то есть
   * интерфейс обещал действие, которое некому выполнить.
   */
  discordEnabled?: boolean;
  onOpenVoice: () => void;
  onOpenOrg: () => void;
  viewOverride: ViewOverride;
  setViewOverride: (v: ViewOverride) => void;
  viewCaptainTeamId: string;
  setViewCaptainTeamId: (v: string) => void;
  teams: Array<{ id: string; name: string }>;
}

/**
 * Панель действий драфт-комнаты: заявки («скоро»), voice, шестерёнка организатора
 * и admin-переключатель просмотра. Название/статус турнира не дублируем — они уже
 * в шапке страницы турнира.
 */
export function DraftHeader({
  resolvedView,
  isSiteAdmin,
  canManage,
  riotDisabledReason,
  discordEnabled,
  onOpenVoice,
  onOpenOrg,
  viewOverride,
  setViewOverride,
  viewCaptainTeamId,
  setViewCaptainTeamId,
  teams,
}: DraftHeaderProps) {
  const tUi = useTranslations("Ui");
  // KAN-366: пока про интеграцию неизвестно — кнопку не предлагаем. Это то же
  // правило, что и в KAN-368 про мигание: неизвестное разрешение не разрешает.
  // Раньше здесь стояло `!== false`, и при незагруженном турнире кнопка успевала
  // появиться — та самая вспышка, которую мы этой ночью и вычищали.
  const showVoice = resolvedView !== "spectator" && discordEnabled === true;
  const showRequests =
    resolvedView === "organizer" || resolvedView === "captain";

  // Карточку с названием турнира не рисуем: имя, статус и формат уже показаны
  // в шапке страницы турнира выше — здесь оставлены только действия (заявки,
  // войс, управление) и админский переключатель вида.
  // Слот появляется вместе с шапкой карточки; пока его нет — рисуем кнопки на месте.
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(document.getElementById(DRAFT_ACTIONS_SLOT_ID));
  }, []);

  const actions = (
      <div className="flex flex-wrap items-center justify-end gap-2">
            <TooltipProvider>
              {showRequests && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={DRAFT_GATED.captainRequests}
                        aria-label={tUi("draftHeader.zayavkiVKomandy")}
                      >
                        <Inbox className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {DRAFT_GATED.captainRequests
                      ? tUi("litDraftHeader.zayavkiVKomandySoon", { hint: tUi(DRAFT_SOON_HINT) })
                      : tUi("litDraftHeader.zayavkiVKomandy")}
                  </TooltipContent>
                </Tooltip>
              )}

              {showVoice && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={onOpenVoice}
                      aria-label="Voice"
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{tUi("draftHeader.golosovyeKanaly")}</TooltipContent>
                </Tooltip>
              )}

              {canManage && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={onOpenOrg}
                      aria-label={tUi("draftHeader.upravlenieDraftom")}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{tUi("draftHeader.upravlenieDraftom")}</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>

            {isSiteAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={viewOverride}
                  onValueChange={(v: ViewOverride) => setViewOverride(v)}
                >
                  <SelectTrigger className="h-9 w-[150px] max-w-full">
                    <SelectValue placeholder={tUi("draftHeader.prosmotr")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{tUi("draftHeader.prosmotrAuto")}</SelectItem>
                    <SelectItem value="organizer">Organizer</SelectItem>
                    <SelectItem value="captain">Captain</SelectItem>
                    <SelectItem value="participant">Participant</SelectItem>
                    <SelectItem value="spectator">Spectator</SelectItem>
                  </SelectContent>
                </Select>
                {viewOverride === "captain" && (
                  <Select
                    value={viewCaptainTeamId}
                    onValueChange={(v) => setViewCaptainTeamId(v)}
                  >
                    <SelectTrigger className="h-9 w-[150px] max-w-full">
                      <SelectValue placeholder={tUi("draftHeader.komanda")} />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
      </div>
  );

  return (
    <>
      {slot ? createPortal(actions, slot) : actions}
      {riotDisabledReason && (
        <Alert>
          <AlertDescription>{riotDisabledReason}</AlertDescription>
        </Alert>
      )}
    </>
  );
}
